import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, Users, Heart, Building2, Award, Target, Eye, ChevronLeft, ChevronRight, Music, HardHat } from 'lucide-react'
import Hero from '../components/Hero'
import ProgramCard from '../components/ProgramCard'
import TestimonialCard from '../components/TestimonialCard'
import NewsletterSignup from '../components/NewsletterSignup'
import SEO from '../components/SEO'

export default function Home() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0)

  const programs = [
    {
      title: 'Theology',
      description: 'Deepen your understanding of Scripture and Christian doctrine. Prepare for ministry with comprehensive theological education.',
      image: '/assets/images/pexels-pixabay-372326.jpg',
      link: '/programs/theology',
      icon: BookOpen
    },
    {
      title: 'Leadership',
      description: 'Develop transformational leadership skills rooted in Christian values. Lead with integrity and vision.',
      image: '/assets/images/pexels-kampus-7551674.jpg',
      link: '/programs/leadership',
      icon: Users
    },
    {
      title: 'Nursing Diploma',
      description: 'Combine compassionate care with clinical excellence. Train to serve communities with professional healthcare.',
      image: '/assets/images/pexels-rdne-6129194.jpg',
      link: '/programs/nursing',
      icon: Heart
    },
    {
      title: 'Music Ministry',
      description: 'Develop spiritually mature and musically equipped ministers who lead worship with excellence and sensitivity to the Holy Spirit.',
      image: '/assets/images/music_image.jpg',
      link: '/programs/music-ministry',
      icon: Music
    },
    {
      title: 'Construction Management',
      description: 'Master the skills to plan, coordinate, and supervise construction projects with professional excellence and integrity.',
      image: '/assets/images/pexels-pixabay-268362.jpg',
      link: '/programs/construction-management',
      icon: HardHat
    }
  ]

  const testimonials = [
    {
      name: 'Pastor Joseph Chikukwa',
      role: 'Alumni - Theology Graduate',
      program: 'Theology Program',
      image: '/assets/images/pexels-joshkjack-135018.jpg',
      quote: 'It was a long walk and there was a lot of transformation in my life, both spiritually and academically. A fruitful journey indeed, and I do not regret. The courses are structured in a way that you enjoy the work. The lecturers will be with you in this endeavor, and you will feel the presence of the Holy Spirit. I have already started registering my church, thanks to PISOM who gave me the confidence. To those who have a passion for God\'s word, please register, you won\'t regret!'
    },
    {
      name: 'Pastor Shadaya',
      role: 'Alumni - Founding Class',
      program: 'Ministry Program',
      image: '/assets/images/pexels-rethaferguson-3825529.jpg',
      quote: 'This is a dream come true. We are so privileged as pioneers. Thank you greatly Prof Mwendesi for this positive mission. Indeed you changed lives through this institution. We are now an enlightened lot. We no longer move in darkness. We have been given proper eyes to see. I\'m humbled to be part of this history-making institution. Hats off to you our esteemed Principal Prof Mwendesi & Dr Mwendesi. May our dear Lord bless you forevermore.'
    },
    {
      name: 'Grace Mutasa',
      role: 'Alumni, Class of 2023',
      program: 'Nursing Diploma',
      image: '/assets/images/pexels-tara-winstead-7723384.jpg',
      quote: 'The nursing program at Rudovia combined excellent clinical training with a heart for service. I\'m now working at a major hospital and making a difference in patients\' lives daily. The program prepared me well for the challenges of healthcare with both professional skills and compassionate care.'
    }
  ]

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [testimonials.length])

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)
  }

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  return (
    <>
      <SEO />
      <div className="pt-20">
        <Hero
          title="Shaping Leaders in Faith, Service, and Innovation"
          subtitle="Empowering the next generation through quality Christian education in Theology, Leadership, Nursing, Music Ministry, and Construction Management"
          image="/assets/images/susan-q-yin-2JIvboGLeho-unsplash.jpg"
          primaryButton={{ text: 'Apply Now', link: '/admissions' }}
          secondaryButton={{ text: 'Explore Programs', link: '/programs' }}
        />

        <section className="section-padding bg-gradient-to-br from-blue-50 to-orange-50">
          <div className="container-custom">
            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="grid md:grid-cols-2 gap-8 items-center p-8 md:p-12">
                  <div className="order-2 md:order-1">
                    <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                      Our Journey: From Phillia to Rudovia
                    </h2>
                    <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                      We are excited to share that our institution, formerly known as <span className="font-bold text-secondary">Phillia International School of Ministry (PISOM)</span>, has evolved into <span className="font-bold text-primary">Rudovia Life College</span>.
                    </p>
                    <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                      This rebranding represents our growth and expanded vision to provide comprehensive, faith-based education across multiple disciplines. While our name has changed, our commitment to excellence, spiritual formation, and transforming lives remains unwavering.
                    </p>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      The eagle in our founding logo symbolizes the vision, strength, and soaring heights we continue to inspire in every student. As Rudovia Life College, we honor our heritage while embracing a broader mission to equip leaders for diverse fields of service.
                    </p>
                  </div>
                  <div className="order-1 md:order-2 flex justify-center">
                    <img 
                      src="/assets/logos/phillia_logo.jpeg" 
                      alt="Phillia International School of Ministry - Our Heritage" 
                      className="rounded-xl shadow-lg max-w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="/assets/images/graduation/graduates-group-photo-indoor-02.jpeg" 
                  alt="Rudovia Life College Graduates" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div>
                <h2 className="heading-2 mb-6">Rudovia Life College</h2>
                <h3 className="text-2xl font-bold text-secondary mb-4">Transforming Lives Through Faith-Based Education</h3>
                <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                  Founded on the principles of faith, excellence, and service, Rudovia Life College is a premier institution dedicated to developing leaders who make a meaningful impact in their communities and across Africa. Our comprehensive programs in Theology, Leadership, Nursing, Music Ministry, and Construction Management are designed to equip students with both academic excellence and spiritual grounding.
                </p>
                <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                  With state-of-the-art facilities, dedicated faculty, and a vibrant campus community, we provide an environment where students can grow intellectually, spiritually, and personally. Our graduates are making a difference in churches, hospitals, businesses, and communities worldwide.
                </p>
                <Link to="/about" className="btn-primary">
                  Learn More About Us
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-2 mb-4">Our Programs</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose from our diverse range of programs designed to equip you for excellence in your calling
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {programs.map((program) => (
                <ProgramCard key={program.title} {...program} />
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-primary text-white">
          <div className="container-custom">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="space-y-4">
                <Award className="w-16 h-16 mx-auto text-secondary" />
                <h3 className="text-3xl font-bold">Our Mission</h3>
                <p className="text-gray-200">
                  To provide transformative Christian education that develops leaders who impact their communities with faith, excellence, and innovation.
                </p>
              </div>
              <div className="space-y-4">
                <Eye className="w-16 h-16 mx-auto text-secondary" />
                <h3 className="text-3xl font-bold">Our Vision</h3>
                <p className="text-gray-200">
                  To be Africa's leading institution for holistic education, producing graduates who are spiritually grounded, academically excellent, and socially responsible.
                </p>
              </div>
              <div className="space-y-4">
                <Target className="w-16 h-16 mx-auto text-secondary" />
                <h3 className="text-3xl font-bold">Our Values</h3>
                <p className="text-gray-200">
                  Faith, Excellence, Integrity, Service, Innovation, and Community - these core values guide everything we do at Rudovia Life College.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-2 mb-4">Student Success Stories</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Hear from our students and alumni about their transformative experiences at Rudovia Life College
              </p>
            </div>
            <div className="relative max-w-4xl mx-auto">
              <div className="overflow-hidden">
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}
                >
                  {testimonials.map((testimonial, index) => (
                    <div key={index} className="w-full flex-shrink-0 px-4">
                      <TestimonialCard {...testimonial} />
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={prevTestimonial}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white rounded-full p-3 shadow-lg hover:bg-secondary hover:text-white transition-colors"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextTestimonial}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white rounded-full p-3 shadow-lg hover:bg-secondary hover:text-white transition-colors"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="flex justify-center mt-8 space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      index === currentTestimonial ? 'bg-secondary' : 'bg-gray-300'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
            <div className="text-center mt-12">
              <Link to="/testimonials" className="btn-outline">
                Read More Stories
              </Link>
            </div>
          </div>
        </section>

        <section className="section-padding bg-gradient-to-br from-secondary to-orange-600 text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Start Your Journey?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Join hundreds of students who are transforming their lives and communities through quality Christian education.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/admissions" className="btn-secondary">
                Apply Today
              </Link>
              <Link to="/contact" className="btn-outline border-white text-white hover:bg-white hover:text-secondary">
                Contact Us
              </Link>
            </div>
          </div>
        </section>

        <NewsletterSignup />
      </div>
    </>
  )
}
